import axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { getCurrentUserIdToken } from 'src/modules/firebase';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useAuth } from 'src/hooks/useAuth';
import { AuthMode } from 'src/contexts/AuthContext';
import { updateLocalStorageUserID } from 'src/utils/usersUtils';
import { markProfileChosen } from 'src/utils/sharedDevice';
import { User } from 'src/types/user.types';
import { Country } from 'src/types/country';
import {
  getErrorCodeName,
  getErrorMessage,
  logInAppPurchase,
} from 'src/modules/purchase/RevenueCat';
import { reportError } from 'src/modules/logs/Sentry';
import { identifyUser, trackPaywallError } from 'src/modules/analytics/analytics';
import { queryKeys } from 'src/queries/keys';

export const useSignupUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      newUserEmail,
      anonymous_token,
      is_anonymous,
      referral_code,
      user_type,
      family_code,
      language,
      country,
    }: {
      newUserEmail: string | null;
      anonymous_token?: string;
      is_anonymous?: boolean;
      referral_code?: string;
      user_type?: string;
      family_code?: string;
      language?: string;
      country?: Country;
    }) => {
      const { data } = await axios.post<User>('/api/sessions/signup', {
        email: newUserEmail,
        firebase_token: await getCurrentUserIdToken(),
        anonymous_token,
        is_anonymous,
        referral_code,
        user_type,
        family_code,
        language,
        country,
      });
      return data;
    },
    onSuccess: async newUser => {
      await queryClient.refetchQueries(queryKeys.user());
      try {
        await logInAppPurchase(newUser, 'signup');
      } catch (error) {
        trackPaywallError({
          operation: 'login',
          error_code: getErrorCodeName(error),
          error_message: getErrorMessage(error),
          user_type: newUser.user_type ?? undefined,
          family_id: newUser.family_id,
          premium_ends_at: newUser.family?.premium_ends_at,
        });
        reportError('Failed to login to RevenueCat after signup', error);
      }
    },
  });
};

export const useOnlogin = () => {
  return useMutation({
    mutationFn: async ({ anonymous_token }: { anonymous_token?: string }) => {
      const { data } = await axios.post<User>('/api/sessions/onlogin', {
        anonymous_token,
      });
      return data;
    },
    onSuccess: async user => {
      try {
        await logInAppPurchase(user, 'login');
      } catch (error) {
        trackPaywallError({
          operation: 'login',
          error_code: getErrorCodeName(error),
          error_message: getErrorMessage(error),
          user_type: user.user_type ?? undefined,
          family_id: user.family_id,
          premium_ends_at: user.family?.premium_ends_at,
        });
        reportError('Failed to login to RevenueCat after login', error);
      }
    },
  });
};

export const useSwitchUser = () => {
  const { setMode } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const { data: customToken } = await axios.post(
        '/api/sessions/switch',
        null,
        { params: { user_id: userId } },
      );
      return customToken as string;
    },
    onSuccess: async (data, userId) => {
      setMode(AuthMode.LOGIN);
      await FirebaseAuthentication.signInWithCustomToken({ token: data });
      // Identify the target before anything renders it, so PostHog starts fetching its arms now.
      const target = queryClient
        .getQueryData<User[]>(queryKeys.users())
        ?.find(member => member.id === userId);
      if (target) identifyUser(target);
      await queryClient.invalidateQueries(queryKeys.user());
      updateLocalStorageUserID(userId);
      markProfileChosen();
      queryClient.clear();
    },
  });
};
